'use client';

import React, { useRef } from 'react';

interface ComparisonCard {
  title: string;
  icon: React.ReactNode;
  before: string[];
  after: string[];
  accentColor: string;
}

const cards: ComparisonCard[] = [
  {
    title: 'Driveways & Concrete',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <path d="M3 9h18M3 15h18M9 3v18M15 3v18" />
      </svg>
    ),
    before: ['Dark stains', 'Tire marks', 'Oil spots', 'Dust buildup', 'Dull curb appeal'],
    after: ['Brighter concrete', 'Cleaner walkways', 'Improved curb appeal', 'More professional appearance', 'Fresh, maintained look'],
    accentColor: '#0ABFBC',
  },
  {
    title: 'Home Exterior',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1H4a1 1 0 01-1-1V9.5z" />
        <path d="M9 21V12h6v9" />
      </svg>
    ),
    before: ['Dusty stucco', 'Cobwebs', 'Dirty trim', 'Weather stains', 'Dull exterior color'],
    after: ['Fresh clean walls', 'Brighter stucco', 'Cleaner windows and trim', 'Improved home appearance', 'Like-new curb appeal'],
    accentColor: '#0ABFBC',
  },
  {
    title: 'Windows, Roofs & Solar',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 3L2 12h3v9h6v-5h2v5h6v-9h3L12 3z" />
        <path d="M9 9h6M9 12h6" />
      </svg>
    ),
    before: ['Water spots', 'Dusty solar panels', 'Dirty gutters', 'Roof streaks', 'Reduced property appearance'],
    after: ['Clear windows', 'Cleaner panels', 'Better drainage', 'Fresher roof appearance', 'More polished property'],
    accentColor: '#0ABFBC',
  },
];

const stats = [
  {
    icon: (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
        <rect x="9" y="3" width="6" height="4" rx="1" />
        <path d="M9 12l2 2 4-4" />
      </svg>
    ),
    label: 'Free Estimates',
  },
  {
    icon: (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3" />
        <path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83" />
      </svg>
    ),
    label: 'Professional Equipment',
  },
  {
    icon: (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
        <path d="M9 22V12h6v10" />
        <rect x="14" y="2" width="8" height="6" rx="1" />
      </svg>
    ),
    label: 'Residential & Commercial',
  },
  {
    icon: (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
      </svg>
    ),
    label: 'Satisfaction Focused',
  },
];

function XIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M15 9l-6 6M9 9l6 6" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0ABFBC" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M8 12l3 3 5-5" />
    </svg>
  );
}

export default function RGPSBeforeAfter() {
  const estimateRef = useRef<HTMLElement | null>(null);

  const scrollToEstimate = () => {
    const el = document.getElementById('estimate') || document.getElementById('contact') || document.querySelector('form');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section
      id="gallery"
      className="py-20 px-4 sm:px-6"
      style={{ background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 60%, #f0fdfc 100%)' }}
    >
      <div className="max-w-6xl mx-auto">

        {/* Section Header */}
        <div className="text-center mb-14">
          <div
            className="inline-flex items-center gap-2 font-bold text-sm px-5 py-2 rounded-full mb-5"
            style={{
              background: 'linear-gradient(135deg, rgba(10,191,188,0.12), rgba(8,145,178,0.12))',
              color: '#0ABFBC',
              border: '1px solid rgba(10,191,188,0.3)',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
            </svg>
            Real Results
          </div>

          <h2
            className="font-bold text-dark-text mb-5 leading-tight"
            style={{
              fontSize: 'clamp(1.85rem, 4.5vw, 3rem)',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              letterSpacing: '-0.02em',
            }}
          >
            The Difference Is{' '}
            <span
              style={{
                background: 'linear-gradient(135deg, #0ABFBC, #0891b2)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              in the Details
            </span>
          </h2>

          <p className="text-gray-text text-lg max-w-2xl mx-auto leading-relaxed">
            Professional exterior cleaning does more than make your property look better. It restores curb appeal, protects surfaces, removes buildup, and helps your home or business make a better first impression.
          </p>
        </div>

        {/* Comparison Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-14">
          {cards.map((card, idx) => (
            <div
              key={idx}
              className="group bg-white rounded-2xl overflow-hidden transition-all duration-300"
              style={{
                boxShadow: '0 4px 24px rgba(0,0,0,0.07)',
                border: '1px solid rgba(10,191,188,0.12)',
              }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLDivElement).style.boxShadow = '0 16px 48px rgba(10,191,188,0.18), 0 4px 16px rgba(0,0,0,0.08)';
                (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-4px)';
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 24px rgba(0,0,0,0.07)';
                (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)';
              }}
            >
              {/* Card Header */}
              <div
                className="px-6 pt-6 pb-5 flex items-center gap-3"
                style={{
                  background: 'linear-gradient(135deg, rgba(10,191,188,0.07) 0%, rgba(8,145,178,0.05) 100%)',
                  borderBottom: '1px solid rgba(10,191,188,0.1)',
                }}
              >
                <div
                  className="flex items-center justify-center w-12 h-12 rounded-xl flex-shrink-0"
                  style={{
                    background: 'linear-gradient(135deg, rgba(10,191,188,0.15), rgba(8,145,178,0.15))',
                    color: '#0ABFBC',
                  }}
                >
                  {card.icon}
                </div>
                <h3
                  className="font-bold text-dark-text text-lg leading-tight"
                  style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                >
                  {card.title}
                </h3>
              </div>

              {/* Before / After Columns */}
              <div className="grid grid-cols-2 divide-x divide-gray-100">
                {/* Before */}
                <div className="px-4 py-5">
                  <div className="flex items-center gap-1.5 mb-4">
                    <span
                      className="text-xs font-bold tracking-widest uppercase px-2.5 py-1 rounded-full"
                      style={{ background: 'rgba(239,68,68,0.08)', color: '#ef4444' }}
                    >
                      Before
                    </span>
                  </div>
                  <ul className="space-y-2.5">
                    {card.before.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-600 leading-snug">
                        <span className="mt-0.5 flex-shrink-0"><XIcon /></span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* After */}
                <div className="px-4 py-5">
                  <div className="flex items-center gap-1.5 mb-4">
                    <span
                      className="text-xs font-bold tracking-widest uppercase px-2.5 py-1 rounded-full"
                      style={{ background: 'rgba(10,191,188,0.1)', color: '#0ABFBC' }}
                    >
                      After
                    </span>
                  </div>
                  <ul className="space-y-2.5">
                    {card.after.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-700 leading-snug font-medium">
                        <span className="mt-0.5 flex-shrink-0"><CheckIcon /></span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Results Strip */}
        <div
          className="rounded-2xl px-6 py-8 mb-14"
          style={{
            background: 'linear-gradient(135deg, #0ABFBC 0%, #0891b2 100%)',
            boxShadow: '0 8px 32px rgba(10,191,188,0.3)',
          }}
        >
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat, idx) => (
              <div key={idx} className="flex flex-col items-center text-center gap-3">
                <div
                  className="flex items-center justify-center w-12 h-12 rounded-xl"
                  style={{ background: 'rgba(255,255,255,0.18)' }}
                >
                  <span className="text-white">{stat.icon}</span>
                </div>
                <span
                  className="text-white font-bold text-sm leading-tight"
                  style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                >
                  {stat.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Block */}
        <div
          className="rounded-2xl px-6 sm:px-10 py-10 text-center"
          style={{
            background: '#ffffff',
            border: '1px solid rgba(10,191,188,0.15)',
            boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
          }}
        >
          <h3
            className="font-bold text-dark-text mb-3 leading-tight"
            style={{
              fontSize: 'clamp(1.4rem, 3vw, 2rem)',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              letterSpacing: '-0.01em',
            }}
          >
            Ready to See What We Can Do{' '}
            <span
              style={{
                background: 'linear-gradient(135deg, #0ABFBC, #0891b2)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              For Your Property?
            </span>
          </h3>

          <p className="text-gray-text text-base max-w-2xl mx-auto mb-8 leading-relaxed">
            Whether you need driveway cleaning, house washing, window cleaning, roof cleaning, solar panel cleaning, gutter cleaning, or trash bin cleaning, Rio Grande Property Solutions is ready to help.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={scrollToEstimate}
              className="w-full sm:w-auto font-bold text-white px-8 py-3.5 rounded-xl transition-all duration-200 text-base"
              style={{
                background: 'linear-gradient(135deg, #0ABFBC 0%, #0891b2 100%)',
                boxShadow: '0 4px 16px rgba(10,191,188,0.35)',
              }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-2px)';
                (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 8px 24px rgba(10,191,188,0.45)';
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(0)';
                (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 4px 16px rgba(10,191,188,0.35)';
              }}
            >
              Get Free Estimate
            </button>

            <a
              href="mailto:rgps505@gmail.com"
              className="w-full sm:w-auto font-bold px-8 py-3.5 rounded-xl transition-all duration-200 text-base text-center"
              style={{
                color: '#0ABFBC',
                border: '2px solid rgba(10,191,188,0.4)',
                background: 'rgba(10,191,188,0.04)',
              }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(10,191,188,0.1)';
                (e.currentTarget as HTMLAnchorElement).style.borderColor = '#0ABFBC';
                (e.currentTarget as HTMLAnchorElement).style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(10,191,188,0.04)';
                (e.currentTarget as HTMLAnchorElement).style.borderColor = 'rgba(10,191,188,0.4)';
                (e.currentTarget as HTMLAnchorElement).style.transform = 'translateY(0)';
              }}
            >
              Email Us
            </a>
          </div>
        </div>

      </div>
    </section>
  );
}
